import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createTenant as createTenantRequest,
  getTenant,
  type TenantCreate,
  type TenantRead,
} from "@harmonyso/tenants-sdk";
import * as z from "zod";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CellSignInButton } from "@/components/cells/cell-failures-alert";
import {
  CELLS,
  PRIMARY_CELL,
  cellRequestOptions,
  findCell,
  type Cell,
} from "@/lib/cells/cells";
import { needsCellSignIn } from "@/lib/cells/cell-session";
import { describeCellMismatch } from "@/lib/cells/cell-tenants";
import { invalidateCellTenantQueries } from "@/lib/cells/cell-queries";
import { TrialDateFields } from "@/components/tenants/trial-date-fields";
import {
  checkTrialDates,
  fromDateInputValue,
  trialDateFormFields,
} from "@/lib/trial";

// Helper function to extract error message from unknown error
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } })
      .response;
    if (response?.data?.detail) {
      return response.data.detail;
    }
  }
  if (error && typeof error === "object" && "message" in error) {
    return (error as { message: string }).message;
  }
  return "Unknown error occurred";
};

const tenantFormSchema = z
  .object({
    cell: z.string().refine((id) => !!findCell(id), "Choose a cell"),
    company: z.string().min(1, "Company name is required"),
    subdomain: z
      .string()
      .min(1, "Subdomain is required")
      .regex(
        /^[a-z0-9-]+$/,
        "Subdomain must only contain lowercase letters, numbers, and hyphens",
      ),
    industry: z.string().optional(),
    size: z.string().optional(),
    subscription: z.string().min(1, "Subscription is required"),
    logo_url: z.string().url().optional().or(z.literal("")),
    ...trialDateFormFields,
  })
  .superRefine(checkTrialDates);

type TenantFormValues = z.infer<typeof tenantFormSchema>;

const CHOSEN_CELL_KEY = "create-tenant.cell";

// The chosen cell survives the round trip through /cell-sign-in. With more
// than one cell there is no default, so a tenant's residency is always picked.
function initialCellId(): string {
  if (CELLS.length === 1) {
    return PRIMARY_CELL.id;
  }
  try {
    const chosen = sessionStorage.getItem(CHOSEN_CELL_KEY);
    return chosen && findCell(chosen) ? chosen : "";
  } catch {
    return "";
  }
}

function rememberChosenCell(cellId: string | null): void {
  try {
    if (cellId) {
      sessionStorage.setItem(CHOSEN_CELL_KEY, cellId);
    } else {
      sessionStorage.removeItem(CHOSEN_CELL_KEY);
    }
  } catch {
    // Remembering the choice is a convenience; the form still works without it.
  }
}

const emptyForm = (): TenantFormValues => ({
  cell: initialCellId(),
  company: "",
  subdomain: "",
  industry: "",
  size: "",
  subscription: "",
  logo_url: "",
  trial_start_date: "",
  trial_end_date: "",
});

interface CreatedTenant {
  cell: Cell;
  tenant?: TenantRead;
  verifyError?: unknown;
}

async function createTenantInCell(
  cell: Cell,
  body: TenantCreate,
): Promise<CreatedTenant> {
  const cellOptions = cellRequestOptions(cell.id);
  const { data: created } = await createTenantRequest({
    ...cellOptions,
    body,
    throwOnError: true,
  });
  try {
    const { data: tenant } = await getTenant({
      ...cellOptions,
      path: { tenant_id: created.tenant_id },
      throwOnError: true,
    });
    return { cell, tenant };
  } catch (verifyError) {
    return { cell, verifyError };
  }
}

interface CreateTenantDialogProps {
  trigger?: React.ReactNode;
}

export function CreateTenantDialog({ trigger }: CreateTenantDialogProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<TenantFormValues>({
    resolver: zodResolver(tenantFormSchema),
    defaultValues: emptyForm(),
  });

  const selectedCell = findCell(form.watch("cell"));
  const cellSignInNeeded = !!selectedCell && needsCellSignIn(selectedCell);

  const createTenant = useMutation({
    mutationFn: ({ cell, body }: { cell: Cell; body: TenantCreate }) =>
      createTenantInCell(cell, body),
    onSuccess: ({ cell, tenant, verifyError }) => {
      invalidateCellTenantQueries(queryClient, cell.id);
      setOpen(false);
      rememberChosenCell(null);
      form.reset(emptyForm());

      if (!tenant) {
        console.error("Failed to verify the new tenant's cell:", verifyError);
        toast.warning(`Tenant created in ${cell.id}`, {
          description: `Couldn't read it back to confirm its cell: ${getErrorMessage(verifyError)}`,
        });
        return;
      }
      const mismatch = describeCellMismatch(tenant, cell);
      if (mismatch) {
        console.error("Tenant created in the wrong cell:", tenant);
        toast.error(`Tenant ${tenant.company} was created in the wrong cell`, {
          description: mismatch,
          duration: Infinity,
        });
        return;
      }
      toast.success(
        CELLS.length > 1
          ? `Tenant created in ${cell.id} (${cell.residency})`
          : "Tenant created successfully!",
      );
    },
    onError: (error: unknown) => {
      console.error("Failed to create tenant:", error);
      const errorDetail = getErrorMessage(error);
      toast.error("Failed to create tenant", {
        description: errorDetail,
      });
    },
  });

  const onSubmit = (values: TenantFormValues) => {
    const cell = findCell(values.cell);
    if (!cell) {
      return;
    }
    createTenant.mutate({
      cell,
      body: {
        company: values.company,
        subdomain: values.subdomain,
        industry: values.industry ?? undefined,
        size: values.size ?? undefined,
        // @ts-expect-error - SubscriptionStatus type mismatch with form string value
        subscription: values.subscription || undefined,
        logo_url: values.logo_url ?? undefined,
        trial_start_date: fromDateInputValue(values.trial_start_date),
        trial_end_date: fromDateInputValue(values.trial_end_date),
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button className="hover:cursor-pointer">
            <Plus className="mr-2 h-4 w-4" />
            Create Tenant
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Tenant</DialogTitle>
          <DialogDescription>
            Add a new tenant configuration to your system.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit(onSubmit)(e);
            }}
            className="space-y-4"
          >
            {CELLS.length > 1 && (
              <FormField
                control={form.control}
                name="cell"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cell</FormLabel>
                    <Select
                      onValueChange={(cellId) => {
                        field.onChange(cellId);
                        rememberChosenCell(cellId);
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a cell" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CELLS.map((cell) => (
                          <SelectItem key={cell.id} value={cell.id}>
                            {cell.id} ({cell.residency})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {selectedCell && cellSignInNeeded
                        ? `Sign in to ${selectedCell.id} before creating a tenant there. Signing in leaves this dialog; your cell choice is kept.`
                        : "Where the tenant's data is stored. This can't be changed later."}
                    </FormDescription>
                    {selectedCell && cellSignInNeeded && (
                      <CellSignInButton cellId={selectedCell.id} />
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="company"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Corporation" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subdomain"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subdomain</FormLabel>
                  <FormControl>
                    <Input placeholder="acme" {...field} />
                  </FormControl>
                  <FormDescription>
                    This will be used in URLs like acme.yourapp.com
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="industry"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Industry (Optional)</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an industry" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                      <SelectItem value="healthcare">Healthcare</SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="manufacturing">
                        Manufacturing
                      </SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Size (Optional)</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select company size" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-1000">
                        201-1000 employees
                      </SelectItem>
                      <SelectItem value="1000+">1000+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subscription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subscription Plan</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select subscription plan" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="free-tier">Free Tier</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="logo_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logo URL (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://example.com/logo.png"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <TrialDateFields control={form.control} />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createTenant.isPending || cellSignInNeeded}
              >
                {createTenant.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create Tenant"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
